/**
 * <PhoneInput> — input that auto-formats SA phone numbers as the user types.
 *
 * Accepts both local format (082 123 4567) and international format
 * (+27 82 123 4567). Stores the canonical form (+27821234567) in state so
 * the backend always sees the same shape.
 *
 * What this is NOT
 * ----------------
 * - It is not libphonenumber. We have one country (South Africa) and one
 *   format. Pulling in the full library would add ~100KB of dead weight.
 * - It does not validate against actual operator prefixes. A typo'd number
 *   that happens to be 10 digits long is accepted at the input layer; the
 *   backend confirms with an OTP / verification email.
 */

import React, { forwardRef } from 'react';

/**
 * Normalise any input to the canonical +27 form.
 * @param {string} raw
 * @returns {string}
 */
function normalise(raw) {
  // Strip everything except digits and a leading +.
  let cleaned = raw.replace(/[^\d+]/g, '');
  // Already canonical (+27...).
  if (cleaned.startsWith('+27')) {
    return cleaned.slice(0, 12); // +27 + 9 digits = 12 chars
  }
  // 0XX form — drop the leading 0, prepend +27.
  if (cleaned.startsWith('0')) {
    return `+27${cleaned.slice(1, 10)}`;
  }
  // No country code, no leading 0 — assume the user is mid-typing the local
  // form and just hasn't typed the leading 0 yet. Keep what they've typed.
  if (cleaned.startsWith('+')) {
    // Some other country code — return as-is, capped at 16 chars.
    return cleaned.slice(0, 16);
  }
  return cleaned.slice(0, 10);
}

/**
 * Format the canonical value back into a human-readable display string.
 * @param {string} canonical — the +27... form (or partial)
 */
function format(canonical) {
  if (canonical.startsWith('+27')) {
    const rest = canonical.slice(3);
    if (rest.length <= 2) return `+27 ${rest}`;
    if (rest.length <= 5) return `+27 ${rest.slice(0, 2)} ${rest.slice(2)}`;
    return `+27 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`;
  }
  return canonical;
}

/**
 * @typedef {object} PhoneInputProps
 * @property {string} value           — canonical (+27...) form
 * @property {(value: string) => void} onChange
 * @property {string} [id]
 * @property {string} [name]
 * @property {string} [placeholder]
 * @property {string} [className]
 */

export const PhoneInput = forwardRef(function PhoneInput(
  {
    value,
    onChange,
    id,
    name = 'phone',
    placeholder = '+27 82 123 4567',
    className = '',
    ...rest
  },
  ref,
) {
  return (
    <input
      ref={ref}
      id={id}
      name={name}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      value={format(value || '')}
      onChange={(e) => onChange(normalise(e.target.value))}
      placeholder={placeholder}
      className={`w-full min-h-touch px-3 py-2 border border-slate-300 rounded-md text-base ${className}`}
      {...rest}
    />
  );
});
